/**
 * POI map bridge.
 * ---------------------------------------------------------------------------
 * Connects the AWAC chat pipeline to the full-screen POI map iframe on the
 * "Neighborhood" page. When the agent emits a PoiMapControl envelope, the
 * conversation layer calls sendMapCommand(command); the bridge
 *   1. asks the app shell to navigate to the Neighborhood page
 *      (window CustomEvent 'awac:show-neighborhood'),
 *   2. queues the command until the map iframe reports ready, then forwards
 *      it via postMessage using the map's protocol:
 *        -> {type:'poi-map:command', id, command:{intent, category, group, name, lang}}
 *        <- {type:'poi-map:ready'} / {type:'poi-map:result', id, ok, ...}
 *
 * Results surface to the visitor as a toast ('awac:toast' CustomEvent —
 * rendered by the Toast component in App.js) so agent-driven map moves are
 * legible: "📍 Costco – LaSalle · 12.1 km" / "no results found".
 *
 * The map page registers its iframe with registerMapFrame() on mount and
 * unregisters on unmount. setMapLanguage() keeps the map's language in sync
 * with the site's FR/EN toggle (silent — no navigation, no toast).
 */
import { util } from "../helpers/common";
import { track, AwacEvents } from "./analytics";

let frameWindow = null;
let frameReady = false;
let pendingCommands = [];
let desiredLang = null;

function postCommand(command) {
    frameWindow.postMessage(
        { type: "poi-map:command", id: util.generateUUID(), command },
        "*"
    );
}

function flushQueue() {
    if (!frameWindow || !frameReady) {
        return;
    }
    if (desiredLang) {
        postCommand({ intent: "setLang", lang: desiredLang });
        desiredLang = null;
    }
    const toSend = pendingCommands;
    pendingCommands = [];
    toSend.forEach(postCommand);
}

function emitToast(detail) {
    window.dispatchEvent(new CustomEvent("awac:toast", { detail }));
}

// Module-level listener: the map posts 'poi-map:ready' on load and in reply
// to 'poi-map:ping'; results come back as 'poi-map:result'.
if (typeof window !== "undefined") {
    window.addEventListener("message", (event) => {
        const data = event.data;
        if (!data || typeof data !== "object") {
            return;
        }
        if (data.type === "poi-map:ready") {
            frameReady = true;
            flushQueue();
        } else if (data.type === "poi-map:ask") {
            // Phone map sheet's "Ask Lia…" row (design handoff §7): open the
            // chat sheet over the map — same event the site CTAs dispatch.
            window.dispatchEvent(new CustomEvent("awac:open-chat", { detail: { source: "map_ask" } }));
        } else if (data.type === "poi-map:back") {
            // Phone map back button: return home and reopen the chat at half
            // (conversation continuity per the handoff).
            window.location.hash = "#/";
            window.dispatchEvent(new CustomEvent("awac:open-chat", { detail: { source: "map_back" } }));
        } else if (data.type === "poi-map:result") {
            track(AwacEvents.MAP_COMMAND_RESULT, {
                ok: !!data.ok,
                intent: data.intent,
                poi: data.poi && data.poi.name
            });
            // Surface the outcome to the visitor (silent for setLang/reset).
            if (data.intent === "showNearest") {
                if (data.ok && data.poi) {
                    emitToast({ kind: "mapFound", name: data.poi.name, km: data.poi.km });
                } else {
                    emitToast({ kind: "mapNone" });
                }
            } else if (data.intent === "showCategory" || data.intent === "showMatches") {
                if (data.ok && data.count === 1 && data.poi) {
                    emitToast({ kind: "mapFound", name: data.poi.name, km: data.poi.km });
                } else if (data.ok && data.count) {
                    emitToast({ kind: "mapCount", count: data.count });
                } else {
                    emitToast({ kind: "mapNone" });
                }
            }
        }
    });
}

/**
 * Register the Neighborhood page's map iframe. Called from the page on mount
 * (and again on iframe load). Pings the map in case it finished loading
 * before we registered.
 * @param {Window} win - iframe.contentWindow
 */
export function registerMapFrame(win) {
    frameWindow = win || null;
    frameReady = false;
    if (frameWindow) {
        try {
            frameWindow.postMessage({ type: "poi-map:ping" }, "*");
        } catch (e) {
            // iframe not ready yet — its own 'poi-map:ready' will arrive on load
        }
    }
}

/** Unregister the map iframe (Neighborhood page unmount). */
export function unregisterMapFrame() {
    frameWindow = null;
    frameReady = false;
}

/**
 * Send an agent command to the POI map, navigating to the Neighborhood page
 * first. Commands sent before the map is ready are queued and flushed on the
 * map's ready handshake.
 * @param {{intent: string, category?: string, group?: string, name?: string, lang?: string}} command
 */
export function sendMapCommand(command) {
    if (!command || typeof command !== "object" || !command.intent) {
        return;
    }
    track(AwacEvents.MAP_COMMAND_SENT, { intent: command.intent, category: command.category, name: command.name });
    pendingCommands.push(command);
    // Ask the app shell to show the map page; it registers the frame when mounted.
    window.dispatchEvent(new CustomEvent("awac:show-neighborhood"));
    flushQueue();
}

/**
 * Keep the map's language in sync with the site's FR/EN toggle. Applied
 * immediately when the map is live, otherwise remembered and applied on the
 * next ready handshake. Never navigates, never toasts.
 * @param {'fr'|'en'} lang
 */
export function setMapLanguage(lang) {
    if (lang !== "fr" && lang !== "en") {
        return;
    }
    if (frameWindow && frameReady) {
        postCommand({ intent: "setLang", lang });
    } else {
        desiredLang = lang;
    }
}

// Console/testing handle — lets you exercise the full agent→map path without
// Salesforce: window.awacPoiMapBridge.sendMapCommand({intent:'showNearest', name:'Costco'})
if (typeof window !== "undefined") {
    window.awacPoiMapBridge = { sendMapCommand, setMapLanguage };
}
